import { Flex, Form, Input, Space, Switch, Tag, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  OrganizationAuthMethodEnum,
  type OrganizationAuthMethod
} from 'src/api/main'
import { getProviderById, providerOrder } from 'src/features/auth/providers'

import { getProviderSetupInstructions } from './authMethodInstructions'
import { PROVIDER_CATALOG_PRESETS } from './authProviderCatalog'
import { useStyles } from './styles'

const { Text } = Typography

type Props = {
  /** Existing saved auth methods — used to show "leaveEmptyToKeep" hints. */
  maskedSecrets?: OrganizationAuthMethod[]
  /**
   * When true, the social-provider `client_secret` becomes required unless a
   * previously saved secret is already stored (detected via
   * `maskedSecrets[].client_secret_masked`). Used by the workspace creation
   * wizard where credentials must be fully provided up front.
   */
  requireClientSecret?: boolean
}

export const AuthMethodsFieldset = ({
  maskedSecrets,
  requireClientSecret = false
}: Props) => {
  const { t } = useTranslation()
  const { styles } = useStyles()

  return (
    <Flex orientation="vertical" gap={12}>
      {providerOrder.map((method) => {
        const saved = maskedSecrets?.find((m) => m.method === method)
        const isSocial =
          method === OrganizationAuthMethodEnum.GOOGLE ||
          method === OrganizationAuthMethodEnum.ENTRAID ||
          method === OrganizationAuthMethodEnum.APPLE

        const protocolLabel = isSocial ? 'OIDC' : null

        return (
          <Flex key={method} orientation="vertical" gap={12}>
            <Flex align="center" justify="space-between">
              <Space size={8}>
                <Text strong>{t(getProviderById(method).i18nKey)}</Text>
                {protocolLabel && (
                  <Text type="secondary">({protocolLabel})</Text>
                )}
              </Space>
              <Form.Item
                name={`${method}_enabled`}
                valuePropName="checked"
                noStyle
              >
                <Switch />
              </Form.Item>
            </Flex>

            {isSocial && (
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev[`${method}_enabled`] !== curr[`${method}_enabled`]
                }
              >
                {({ getFieldValue }) => {
                  if (!getFieldValue(`${method}_enabled`)) {
                    return null
                  }
                  const setupInstructions = getProviderSetupInstructions(method)

                  return (
                    <Flex orientation="vertical" gap={12}>
                      {setupInstructions && (
                        <Flex
                          orientation="vertical"
                          gap={8}
                          className={styles.socialSetupInstructions}
                        >
                          <Text strong>
                            {t('authMethods.setupInstructionsTitle')}
                          </Text>
                          <Text type="secondary">
                            {t(setupInstructions.hintKey)}
                          </Text>
                          <Text>
                            {t('authMethods.docsLabel')}{' '}
                            <a
                              href={setupInstructions.docsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t('authMethods.openDocs')}
                            </a>
                          </Text>
                          <Flex orientation="vertical" gap={4}>
                            <Text strong>
                              {t('authMethods.redirectUrlsTitle')}
                            </Text>
                            {setupInstructions.redirectUrlGroups.map(
                              (group) => (
                                <Flex
                                  key={group.labelKey}
                                  orientation="vertical"
                                  gap={4}
                                >
                                  <Text type="secondary">
                                    {t(group.labelKey)}
                                  </Text>
                                  {group.urls.map((url) => (
                                    <Text
                                      key={url}
                                      code
                                      className={styles.callbackUrlText}
                                    >
                                      {url}
                                    </Text>
                                  ))}
                                </Flex>
                              )
                            )}
                          </Flex>
                        </Flex>
                      )}

                      <Form.Item
                        name={`${method}_client_id`}
                        label={
                          method === OrganizationAuthMethodEnum.APPLE
                            ? t('authMethods.appleServiceId')
                            : t('authMethods.clientId')
                        }
                        rules={[
                          {
                            required: true,
                            message: t('required_field_error')
                          }
                        ]}
                      >
                        <Input
                          placeholder={
                            method === OrganizationAuthMethodEnum.GOOGLE
                              ? 'xxx.apps.googleusercontent.com'
                              : method === OrganizationAuthMethodEnum.APPLE
                                ? 'com.example.service'
                                : undefined
                          }
                        />
                      </Form.Item>

                      <Form.Item
                        name={`${method}_client_secret`}
                        label={
                          method === OrganizationAuthMethodEnum.APPLE
                            ? t('authMethods.applePrivateKey')
                            : t('authMethods.clientSecret')
                        }
                        rules={
                          requireClientSecret && !saved?.client_secret_masked
                            ? [
                                {
                                  required: true,
                                  message: t('required_field_error')
                                }
                              ]
                            : undefined
                        }
                      >
                        <Input.Password
                          placeholder={
                            saved?.client_secret_masked
                              ? method === OrganizationAuthMethodEnum.APPLE
                                ? t('authMethods.leaveEmptyToKeepKey')
                                : t('authMethods.leaveEmptyToKeep')
                              : undefined
                          }
                          autoComplete="new-password"
                        />
                      </Form.Item>

                      {method === OrganizationAuthMethodEnum.ENTRAID && (
                        <Form.Item
                          name={`${method}_tenant_id`}
                          label={t('authMethods.tenantId')}
                          rules={[
                            {
                              required: true,
                              message: t('required_field_error')
                            }
                          ]}
                        >
                          <Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                        </Form.Item>
                      )}

                      {method === OrganizationAuthMethodEnum.APPLE && (
                        <>
                          <Form.Item
                            name={`${method}_tenant_id`}
                            label={t('authMethods.teamId')}
                            rules={[
                              {
                                required: true,
                                message: t('required_field_error')
                              }
                            ]}
                          >
                            <Input placeholder="XXXXXXXXXX" />
                          </Form.Item>
                          <Form.Item
                            name={`${method}_key_id`}
                            label={t('authMethods.keyId')}
                            rules={[
                              {
                                required: true,
                                message: t('required_field_error')
                              }
                            ]}
                          >
                            <Input placeholder="XXXXXXXXXX" />
                          </Form.Item>
                        </>
                      )}
                    </Flex>
                  )
                }}
              </Form.Item>
            )}
          </Flex>
        )
      })}

      {PROVIDER_CATALOG_PRESETS.map((preset) => (
        <Flex
          key={preset.id}
          align="center"
          justify="space-between"
          style={{ opacity: 0.5 }}
        >
          <Space size={8}>
            <Text strong>{t(preset.labelKey)}</Text>
            <Text type="secondary">({preset.protocol.toUpperCase()})</Text>
            <Tag color="warning">{t('soon')}</Tag>
          </Space>
          <Switch disabled checked={false} />
        </Flex>
      ))}
    </Flex>
  )
}
